






























import { SfLoader, SfHeading } from '@storefront-ui/vue';
import SfBreadcrumbs from "~/components/Sf/SfBreadcrumbs.vue";
import {
  defineComponent,
  ref,
  useFetch,
  useContext,
  useRouter,
  useRoute,
  onMounted,
} from '@nuxtjs/composition-api';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { useContent, useGtm } from '~/composables';
import type { CmsPage } from '~/modules/GraphQL/types';
import { usePageStore } from '~/stores/page';
import PageBuilder from '~/components/PageBuilder/PageBuilder.vue';
import { Breadcrumb } from '~/modules/catalog/types';
import SearchBar from '~/components/Cms/SearchBar.vue';
import NewsList from '~/components/Cms/NewsList.vue';
import MenuContext from '~/components/Cms/MenuContext.vue';
import { isEmpty, map, assign, join, trimEnd } from 'lodash-es';
import Vue from "vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

Vue.component("VSelect", vSelect);

export default defineComponent({
  name: 'CmsPage',
  components: {
    SearchBar,
    PageBuilder,
    SfLoader,
    SfHeading,
    SfBreadcrumbs,
    NewsList,
    MenuContext,
    Banner: () => import(/* webpackPrefetch: true */ '~/components/General/Banner.vue'),
  },
  setup() {
    const { eventPageLoad } = useGtm();
    const route = useRoute();
    const router = useRouter();
    const pageStore = usePageStore();
    const { routeData } = usePageStore();
    const { addTags } = useCache();
    const { localePath, app: { i18n }, $config } = useContext();
    const { error: nuxtError } = useContext();
    const topBannerImage = ref(null);
    const bannerData = ref({});
    const {
      loadCmsPage,
      loading,
      error,
    } = useContent();

    const page = ref<CmsPage | null>(null);

    const breadcrumbs = ref<Breadcrumb[]>([]);
    const pageLevel = ref(null);
    const pageHeaderRender = ref(null);
    const pageBodyClassNames = ref([]);
    const menuContext = ref(null);
    const isServer = () => typeof window === `undefined`;
    const serverGet = ref(false);

    useFetch(async () => {
      if (isServer) {
        serverGet.value = true;
      }
      const routerIdentifier = router.currentRoute.params.slug ?? routeData.identifier;
      const data = await loadCmsPage({
        identifier: routerIdentifier,
      });
      // Define page level
      const cmsPageLevel = data['level'] ?? '1';
      pageLevel.value = cmsPageLevel as string;
      const pageLevelClassName = 'page-level-' + cmsPageLevel as string;

      // Define page header render
      const cmsPageHeaderRender = data['page_header_render'] ?? 'default';
      pageHeaderRender.value = cmsPageHeaderRender as string;
      const pageHeaderRenderClassName = 'page-header-' + cmsPageHeaderRender as string;

      // Set page body class
      pageBodyClassNames.value = [pageLevelClassName, pageHeaderRenderClassName];

      if (!isEmpty(data['breadcrumbs'])) {
        // Add cms hierarchy xpath as breadcrumbs
        breadcrumbs.value = map(data['breadcrumbs'], (item) => {
          return {
            text: item.title,
            link: localePath('/') + item.url_key,
          };
        });
        // Add home page as first element
        breadcrumbs.value.unshift({
          text: i18n.t('Home') as string,
          link: localePath('/'),
        });
      }
      if (!isEmpty(data['top_banner_background_image'])) {
        topBannerImage.value = data['top_banner_background_image'] ?? '';
      }
      bannerData.value = {
        imgUrl: data['top_banner_background_image'] ?? '',
        subTitle: data['subtitle'] ?? '',
        shortDescription: data['short_description'] ?? '',
      };
      page.value = assign(data, {
        pageTitle: data['page_title'],
        metaDescription: data['meta_description'],
        pageUrl: trimEnd($config.baseUrl, '/') + route.value.path,
      });

      menuContext.value = data['menu_context'];

      if (error?.value?.page || !page.value) nuxtError({ statusCode: 404 });

      addTags([{ prefix: CacheTagPrefix.View, value: routeData.identifier }]);

      pageStore.$patch((state) => {
        state.pageData = assign(state.pageData, {
          pageName: page.value['page_title'],
          pageTitle: page.value['page_title'],
          pageCategory: join(map(breadcrumbs.value, (item) => item.text), '/'),
          breadCrumbs: map(breadcrumbs.value, (item) => item.text),
        });
      });

      if (!isServer()) {
        eventPageLoad();
      }
    });

    onMounted(() => {
      if (!serverGet.value) {
        eventPageLoad();
      }
    });

    return {
      topBannerImage,
      breadcrumbs,
      page,
      loading,
      bannerData,
      pageLevel,
      pageHeaderRender,
      pageBodyClassNames,
      menuContext
    };
  },
  head() {
    return getMetaInfo(this.page);
  },
  mounted() {
    // Remove all old body classes
    document.body.classList.forEach((element) => {
      if (element !== 'show-hero-img') {
        document.body.classList.remove(element);
      }
    });

    // Set current page level and header render for html body element
    if (this.pageBodyClassNames && Array.isArray(this.pageBodyClassNames)) {
      this.pageBodyClassNames.forEach((element) => {
        document.body.classList.add(element);
      });
    }
  },
  updated() {
    // Remove all old body classes
    document.body.classList.forEach((element) => {
      if (element !== 'show-hero-img') {
        document.body.classList.remove(element);
      }
    });

    // Set current page level and header render for html body element
    if (this.pageBodyClassNames && Array.isArray(this.pageBodyClassNames)) {
      this.pageBodyClassNames.forEach((element) => {
        document.body.classList.add(element);
      });
    }
  },
});
